import { type ReadonlyVec2, vec2, mat2d, glMatrix } from 'gl-matrix';

import { bezierToPointsByTParameter } from '@canvas/bezier/points';
import { boxCenter, boxSize, type Box, type ReadonlyBox } from '@canvas/math/box';
import { boxToMat2dRotatedNormalized } from '@canvas/math/box/transform';
import { mat2dextended } from '@canvas/math/mat2d';

import { type Graphic } from './graphic';
import { type Axis, GraphicPoint, type ReadonlyGraphicPoint } from './points';
import {
  GraphicCubicBezier,
  GraphicLine,
  type GraphicSection,
  GraphicSubPathStart,
} from './types';

/**
 * The maximum allowed error tolerance when converting a Bezier curve of a graphic to a
 * series of points. Lower values are more accurate, but use more points.
 * The number of points produced is capped by BEZIER_CURVE_MAX_SUBDIVISIONS
 */
const BEZIER_CURVE_ERROR_TOLERANCE = 0.0005;

/**
 * The maximum number of subdivisions that a bezier curve can be broken into.
 * This should be equal to CURVE_LENGTH_MAX_ITERATIONS in graphics-stroke.vert.
 * If it is higher than CURVE_LENGTH_MAX_ITERATIONS then dashed lines will have visual artifacts.
 */
export const BEZIER_CURVE_MAX_SUBDIVISIONS = 32;
const BEZIER_CURVE_MAX_SUBDIVISION_DEPTH = Math.log2(BEZIER_CURVE_MAX_SUBDIVISIONS);

/**
 * Corner types dictate how dashed patterns are rendered over multiple segments,
 * used by `graphics-stroke.vert`
 */
export enum CornerType {
  /**
   * Skips rendering the data at the corresponding vertex.
   */
  SKIP = 0,
  /**
   * The same as {@link SKIP}, but indicates that the current curve will continue in the
   * next data texture block. The vertex shader relies on this being equal to `SKIP + 1`.
   */
  SKIP_BREAK = 1,
  /**
   * Treats the corresponding vertex as a sharp corner,
   * Dashed patterns on adjacent segments are calculated separately
   * and don't affect each other.
   */
  CORNER = 2,
  /**
   * Treats the corresponding vertex as a smooth corner.
   * Dashed patterns are continued across multiple adjacent segments,
   * when the corner types are smooth.
   */
  SMOOTH = 3,
}

const pointFlyweight = GraphicPoint.create();
const vecFlyweight = vec2.create();

/**
 * Creates a matrix that converts a point in graphic-space ((0, 0) is top-left, (1,1) is
 * bottom-right) to world-space.
 *
 * @param graphicBoundingBox The bounding box of the graphic in world space.
 * @param graphicRotation Optional, the rotation of the graphic, clockwise in radians.
 * Defaults to 0.
 * @param graphicRotationOrigin Optional, the origin point of the rotation in world
 * space. Defaults to the centre of graphicsBoundingBox.
 * @returns The transform matrix.
 */
export function graphicSpaceToWorldSpaceMatrix(
  graphicBoundingBox: Box,
  graphicRotation = 0,
  graphicRotationOrigin?: ReadonlyVec2,
) {
  return boxToMat2dRotatedNormalized(
    graphicBoundingBox,
    graphicRotation,
    graphicRotationOrigin,
  );
}

/**
 * Creates a matrix that converts a point in graphic-space to world-space. Uses
 * unnormalized graphic space coordinates, where `graphicBoundingBox` defines the
 * top-left and bottom-right corners.
 *
 * @param graphicBoundingBox The bounding box of the graphic in world space.
 * @param graphicRotation Optional, the rotation of the graphic, clockwise in radians.
 * Defaults to 0.
 * @param graphicRotationOrigin Optional, the origin point of the rotation in world
 * space. Defaults to the centre of graphicsBoundingBox.
 * @returns The transform matrix.
 */
export function graphicSpaceToWorldSpaceUnnormalizedMatrix(
  graphicBoundingBox: Box,
  graphicRotation = 0,
  graphicRotationOrigin?: ReadonlyVec2,
) {
  const m = mat2d.create();
  return mat2dextended.rotateAround(
    m,
    m,
    graphicRotation,
    graphicRotationOrigin ?? boxCenter(graphicBoundingBox),
  );
}

/**
 * Creates a matrix that converts a point in world-space to graphic-space ((0, 0) is
 * top-left, (1,1) is bottom-right).
 *
 * @param graphicBoundingBox The bounding box of the shape on the board.
 * @param graphicRotation Optional, the rotation of the graphic, clockwise in radians.
 * Defaults to 0.
 * @param graphicRotationOrigin Optional, the origin point of the rotation in world
 * space. Defaults to the centre of graphicsBoundingBox.

 * @returns The transform matrix.
 */
export function worldSpaceToGraphicSpaceMatrix(
  graphicBoundingBox: Box,
  graphicRotation = 0,
  graphicRotationOrigin?: ReadonlyVec2,
) {
  const transform = graphicSpaceToWorldSpaceMatrix(
    graphicBoundingBox,
    graphicRotation,
    graphicRotationOrigin,
  );
  mat2d.invert(transform, transform);
  return transform;
}

/**
 * Creates a matrix that converts a point in world-space to graphic-space. Uses
 * unnormalized graphic space coordinates, where `graphicBoundingBox` defines the
 * top-left and bottom-right corners.
 *
 * @param graphicBoundingBox The bounding box of the shape on the board.
 * @param graphicRotation Optional, the rotation of the graphic, clockwise in radians.
 * Defaults to 0.
 * @param graphicRotationOrigin Optional, the origin point of the rotation in world
 * space. Defaults to the centre of graphicsBoundingBox.

 * @returns The transform matrix.
 */
export function worldSpaceToGraphicSpaceUnnormalizedMatrix(
  graphicBoundingBox: Box,
  graphicRotation = 0,
  graphicRotationOrigin?: ReadonlyVec2,
) {
  const transform = graphicSpaceToWorldSpaceUnnormalizedMatrix(
    graphicBoundingBox,
    graphicRotation,
    graphicRotationOrigin,
  );
  mat2d.invert(transform, transform);
  return transform;
}

/**
 * Given a graphic describing a shape on the board, and the bounding box of
 * that shape, randomly selects a point on the outline of the shape. The
 * distribution is not necessarily uniform over the length of the outline, but
 * has a non-zero chance of selecting any such valid point.
 *
 * @param graphic A graphic describing a shape on the board
 * @param boundingBox The bounding box of that shape
 * @returns A random point on the outline of the shape, with coordinates given in world-space
 */
export const getRandomEdgePositionFromGraphic = (
  graphic: Graphic,
  boundingBox: ReadonlyBox,
) => {
  // Get a random fill
  const randomFill = graphic.fills[Math.floor(Math.random() * graphic.fills.length)];
  // Get a random segment on that fill
  let randomSegment: GraphicCubicBezier | GraphicLine | GraphicSubPathStart | undefined =
    undefined;
  while (!randomSegment || randomSegment instanceof GraphicSubPathStart) {
    // If we randomly selected a sub-path start, we need to try again
    randomSegment =
      randomFill.segments[Math.floor(Math.random() * randomFill.segments.length)];
  }

  // Get a random point on that segment
  if (randomSegment instanceof GraphicCubicBezier) {
    // Get a random point on the bezier. The distribution won't be perfectly
    // uniform over length because we're using a random value of `t` to choose
    // the point, but we're already getting a non-uniform distribution over
    // length by randomly selecting a fill/segment by index.
    randomSegment.getPoint(pointFlyweight, Math.random());
  } else {
    GraphicPoint.interpolate(
      pointFlyweight,
      randomSegment.start,
      randomSegment.end,
      Math.random(),
    );
  }

  const pointInGraphicSpace = pointFlyweight.resolveToNormalisedScale(
    vec2.create(),
    boxSize(boundingBox),
  );

  // Convert to world-space
  const transform = graphicSpaceToWorldSpaceMatrix(boundingBox);
  return vec2.transformMat2d(pointInGraphicSpace, pointInGraphicSpace, transform);
};

/**
 * Appends a corner type to an array of corner types, ensuring that we don't exceed
 * the maximum number of smooth corners in a row.
 *
 * @param cornerTypes The array of corner types to append to.
 * @param newCornerType The new corner type to append.
 * @param smoothCornerStreak The current number of smooth corners in a row.
 * @returns The updated number of smooth corners in a row.
 */
function appendCornerType(
  cornerTypes: CornerType[],
  newCornerType: CornerType,
  smoothCornerStreak: number,
): number {
  if (newCornerType === CornerType.SMOOTH) {
    smoothCornerStreak++;
  } else {
    smoothCornerStreak = 0;
  }

  if (smoothCornerStreak > BEZIER_CURVE_MAX_SUBDIVISIONS) {
    // If we have too many smooth corners in a row, downgrade this to a corner
    cornerTypes.push(CornerType.CORNER);
    return 0;
  } else {
    cornerTypes.push(newCornerType);
    return smoothCornerStreak;
  }
}

/**
 * Takes a Graphic fill/stroke and produces a series of points describing the
 * outline/path. All fills will be treated as though they are closed. These will be
 * treated as vertices of a mesh.
 *
 * @param section A connected fill/stroke to find the outline/path of
 * @param includeLastPoint If `true`, the last point of each polygon will be included in
 * the output. This is useful for rendering the outline of a shape, but not for rendering
 * the fill of a shape, as it will duplicate the first point of the polygon.
 * @returns An array of polygons describing the outline of the graphic. Each polygon is
 * an array of vertices as vec2s. A polygon is created for each sub-path in the section.
 */
export function getSectionVertices(
  section: GraphicSection,
  naturalGraphicSize: ReadonlyVec2,
  includeLastPoint = false,
): {
  polygons: ReadonlyGraphicPoint[][];
  cornerTypes: CornerType[][];
} {
  const { segments } = section;

  const polygons: ReadonlyGraphicPoint[][] = [];
  const cornerTypes: CornerType[][] = [];
  let currentPolygon: ReadonlyGraphicPoint[] = [];
  let currentCornerTypes: CornerType[] = [];

  // Keeps track of how many SMOOTH corners we've seen in a row, so we can limit them to
  // BEZIER_CURVE_MAX_SUBDIVISIONS
  let smoothCornerStreak = 0;

  // Stores the points of a single Bezier curve as we iterate through the segments.
  // Avoids recreating the array on each iteration.
  const bezierTParameters: number[] = [];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (segment instanceof GraphicCubicBezier) {
      bezierTParameters.length = 0;
      bezierToPointsByTParameter(
        bezierTParameters,
        segment.resolveToNormalisedScale(naturalGraphicSize),
        BEZIER_CURVE_ERROR_TOLERANCE,
        BEZIER_CURVE_MAX_SUBDIVISION_DEPTH,
      );

      for (let j = 0; j < bezierTParameters.length - 1; j++) {
        currentPolygon.push(
          segment.getPoint(GraphicPoint.create(), bezierTParameters[j]),
        );

        const cornerType =
          j === 0 && !segment.options?.continuesFromPrevious
            ? CornerType.CORNER
            : CornerType.SMOOTH;
        smoothCornerStreak = appendCornerType(
          currentCornerTypes,
          cornerType,
          smoothCornerStreak,
        );
      }
    } else if (segment instanceof GraphicLine) {
      // Add the lines start point to the output array
      currentPolygon.push(segment.start);

      const cornerType = segment.options?.continuesFromPrevious
        ? CornerType.SMOOTH
        : CornerType.CORNER;
      smoothCornerStreak = appendCornerType(
        currentCornerTypes,
        cornerType,
        smoothCornerStreak,
      );
    } else if (segment instanceof GraphicSubPathStart) {
      // If we encounter a sub-path start, we need to add the last point of the
      // previous segment to the output array, as it is the end of the previous
      // sub-path and the start of the new one.
      const previousSegment = segments[i - 1];
      if (previousSegment && !(previousSegment instanceof GraphicSubPathStart)) {
        if (
          includeLastPoint ||
          !GraphicPoint.equals(previousSegment.end, currentPolygon[0])
        ) {
          currentPolygon.push(previousSegment.end);

          smoothCornerStreak = appendCornerType(
            currentCornerTypes,
            CornerType.CORNER,
            smoothCornerStreak,
          );
        }

        // Start a new polygon for the new sub-path
        polygons.push(currentPolygon);
        cornerTypes.push(currentCornerTypes);
        currentPolygon = [];
        currentCornerTypes = [];
      }
    }
  }

  // Add the final segment's end point to the output array
  const lastSegment = segments[segments.length - 1];
  if (lastSegment && !(lastSegment instanceof GraphicSubPathStart)) {
    if (includeLastPoint || !GraphicPoint.equals(lastSegment.end, currentPolygon[0])) {
      currentPolygon.push(lastSegment.end);
      smoothCornerStreak = appendCornerType(
        currentCornerTypes,
        CornerType.CORNER,
        smoothCornerStreak,
      );
    }
    polygons.push(currentPolygon);
    cornerTypes.push(currentCornerTypes);
  }

  return { polygons, cornerTypes };
}

/**
 * Generate lines for the outline/path of a fill or stroke.
 * @param graphic The graphic this section belongs to
 * @param section A fill/stroke to find the outline/path of
 * @returns A generator that yields lines describing the outline/path of the section
 */
export function* getSectionAsLines(
  graphic: Graphic,
  section: GraphicSection,
): IterableIterator<GraphicLine> {
  const { polygons } = getSectionVertices(section, graphic.naturalSize, true);

  // Create lines from the points.
  for (const polygon of polygons) {
    for (let vertexIndex = 1; vertexIndex < polygon.length; vertexIndex++) {
      const lastVertexIndex = vertexIndex - 1;

      const start = polygon[lastVertexIndex];
      const end = polygon[vertexIndex];
      yield new GraphicLine(start, end);
    }
  }
}

/**
 * Given a graphic, a point, and an axis to raycast along, returns the closest
 * point (in either direction) at which the raycast first intersects the
 * boundaries of a fill of the graphic.
 *
 * Multiple            No intersections
 * intersections,      as line does not
 * uses closest        intersect graphic
 *     ┌────┐           ▲ ┌────┐
 *     │    │           │ │    │
 * ◄─p─x────┼─►         p │    │
 *     │    │           │ │    │
 *     │    │           │ │    │
 *     └────┘           ▼ └────┘
 *
 * Intersections in both directions,
 * uses closest regardless of direction
 *   ┌───────────────┐
 *   │    ┌─────┐    │
 *   │    │     │    │
 *   │    │     │    │
 * ◄─┼────x─p───┼────┼─►
 *   └────┘     └────┘
 *
 * @param graphic The graphic to test intersection with
 * @param point The point to raycast from
 * @param axis The axis to raycast along
 * @param resolutionOverCubicBeziers The number of points to divide cubic beziers into.
 * Higher values increase intersection accuracy on sharper corners but take longer to run.
 *
 * @returns The first intersection point on the interval, or undefined if the
 * interval does not intersect the graphic or no intersection point is
 * representable as a graphic point
 */
export function raycastFromPointOntoFillBoundaries(
  graphic: Graphic,
  point: GraphicPoint,
  axis: Axis,
  resolutionOverCubicBeziers = 5,
): GraphicPoint | undefined {
  // We use resolution at the graphic's natural size to determine the closest point
  const resolvedPoint = point.resolveToWorldScale(vec2.create(), graphic.naturalSize);

  // If we want to raycast along the x-axis, the y-value is fixed
  const axisWithFixedValue = axis === 'x' ? 'y' : 'x';
  const valueOnAxis =
    axisWithFixedValue === 'x'
      ? { pos: point.xPos(), offset: point.xOffset() }
      : { pos: point.yPos(), offset: point.yOffset() };

  // Check all intersections of any segment and the interval and keep only
  // the closest to the start of the interval
  let firstIntersection: GraphicPoint | undefined;
  let smallestDistance = Number.POSITIVE_INFINITY;

  const checkIfShouldStoreIntersectionResult = (
    intersection: ReturnType<typeof getPointOnEdgeFromValueOnAxis>,
  ) => {
    if (!('failureReason' in intersection)) {
      const distance = vec2.distance(
        resolvedPoint,
        intersection.resolveToWorldScale(vecFlyweight, graphic.naturalSize),
      );

      if (distance < smallestDistance) {
        firstIntersection = intersection;
        smallestDistance = distance;
      }
    }
  };

  for (const fill of graphic.fills) {
    for (const segment of fill.segments) {
      if (segment instanceof GraphicCubicBezier) {
        // A curve and a line can have multiple intersections, we want to
        // consider each of them
        let prev: GraphicPoint | undefined;
        segment.walkPoints(resolutionOverCubicBeziers, (point) => {
          if (prev) {
            checkIfShouldStoreIntersectionResult(
              getPointOnEdgeFromValueOnAxis(prev, point, axisWithFixedValue, valueOnAxis),
            );
            GraphicPoint.copy(prev, point);
          } else {
            prev = GraphicPoint.copy(pointFlyweight, point);
          }
        });
      } else if (segment instanceof GraphicLine) {
        // Two lines can only have 0 or 1 intersections
        checkIfShouldStoreIntersectionResult(
          getPointOnEdgeFromValueOnAxis(
            segment.start,
            segment.end,
            axisWithFixedValue,
            valueOnAxis,
          ),
        );
      }
    }
  }

  return firstIntersection;
}

export function getPointOnEdgeFromValueOnAxis(
  edgeStart: ReadonlyGraphicPoint,
  edgeEnd: ReadonlyGraphicPoint,
  axis: Axis,
  value: number | { pos: number; offset: number },
  allowProjection = false,
):
  | GraphicPoint
  | {
      failureReason: 'not representable';
      posProportion: number;
      offsetProportion: number;
    }
  | { failureReason: 'no intersection' } {
  const valuePos = typeof value === 'number' ? value : value.pos;
  const valueOffset = typeof value === 'number' ? 0 : value.offset;

  let proportionAlongEdge: number | undefined = undefined;

  let startPos: number;
  let endPos: number;
  let startOffset: number;
  let endOffset: number;

  if (axis === 'x') {
    if (allowProjection && GraphicPoint.isHorizontal(edgeStart, edgeEnd)) {
      // If the edge is horizontal, all points have the same y, so we can just
      // take that value. This may give a point outside of the interval
      // provided, so we only do this when projection is allowed.
      const result = GraphicPoint.create();
      GraphicPoint.setForAxis(result, 'x', valuePos, valueOffset);
      GraphicPoint.copyPointsByAxis(result, { y: edgeStart });
      return result;
    }

    startPos = edgeStart.xPos();
    endPos = edgeEnd.xPos();
    startOffset = edgeStart.xOffset();
    endOffset = edgeEnd.xOffset();
  } else {
    if (allowProjection && GraphicPoint.isVertical(edgeStart, edgeEnd)) {
      // If the edge is vertical, all points have the same x, so we can just
      // take that value. This may give a point outside of the interval
      // provided, so we only do this when projection is allowed.
      const result = GraphicPoint.create();
      GraphicPoint.copyPointsByAxis(result, { x: edgeStart });
      GraphicPoint.setForAxis(result, 'y', valuePos, valueOffset);
      return result;
    }

    startPos = edgeStart.yPos();
    endPos = edgeEnd.yPos();
    startOffset = edgeStart.yOffset();
    endOffset = edgeEnd.yOffset();
  }

  // Try to get proportion from positions
  if (!glMatrix.equals(startPos, endPos)) {
    proportionAlongEdge = (valuePos - startPos) / (endPos - startPos);
  }

  // Try to get proportion from offsets
  if (!glMatrix.equals(startOffset, endOffset)) {
    const proportionAlongEdgeFromOffsets =
      (valueOffset - startOffset) / (endOffset - startOffset);

    // If the positions and offsets give different proportions, we can't
    // represent the result as a GraphicPoint, so throw an exception
    if (
      proportionAlongEdge !== undefined &&
      !glMatrix.equals(proportionAlongEdge, proportionAlongEdgeFromOffsets)
    ) {
      return {
        failureReason: 'not representable',
        posProportion: proportionAlongEdge,
        offsetProportion: proportionAlongEdgeFromOffsets,
      };
    }

    proportionAlongEdge = proportionAlongEdgeFromOffsets;
  }

  if (proportionAlongEdge === undefined) {
    return { failureReason: 'no intersection' };
  }

  if (!allowProjection && (proportionAlongEdge < 0 || proportionAlongEdge > 1)) {
    return { failureReason: 'no intersection' };
  }

  return GraphicPoint.interpolate(
    GraphicPoint.create(),
    edgeStart,
    edgeEnd,
    proportionAlongEdge,
  );
}

export const textAreaSizeToShapeSize = (graphic: Graphic, rectangleSize: ReadonlyVec2) =>
  graphic.textArea.shapeSizeFromSize.resolveToWorldScale(vec2.create(), rectangleSize);

export const shapeSizeToTextAreaSize = (graphic: Graphic, shapeSize: ReadonlyVec2) =>
  graphic.textArea.sizeFromShapeSize.resolveToWorldScale(vec2.create(), shapeSize);

export const shapeSizeToTextAreaCenterOffset = (
  graphic: Graphic,
  shapeSize: ReadonlyVec2,
) =>
  graphic.textArea.centerOffsetFromShapeSize.resolveToWorldScale(
    vec2.create(),
    shapeSize,
  );

/**
 * Used to calculate the size of a graphic from its natural size and a width
 * when the height is unknown
 * @param naturalSize the natural size of the graphic
 * @param width the width to fit the graphic within
 * @returns size of the graphic
 */
export const getHeightFromAspectRatioAndWidth = (
  naturalAspectRatio: number,
  width: number,
) => {
  return vec2.fromValues(width, width / naturalAspectRatio);
};

export const boundingBoxSizeToVerticallyExteriorTextAreaSize = (
  aspectRatio: number,
  boundingBoxSize: ReadonlyVec2,
) => {
  const graphicSize = getHeightFromAspectRatioAndWidth(aspectRatio, boundingBoxSize[0]);

  // Text area size is the bounding box size minus the graphic size
  return vec2.set(graphicSize, graphicSize[0], boundingBoxSize[1] - graphicSize[1]);
};

export const verticallyExteriorTextAreaSizeToBoundingBoxSize = (
  aspectRatio: number,
  textAreaSize: ReadonlyVec2,
) => {
  const graphicSize = getHeightFromAspectRatioAndWidth(aspectRatio, textAreaSize[0]);

  // Bounding box size is the text area size plus the graphic size
  return vec2.set(graphicSize, textAreaSize[0], textAreaSize[1] + graphicSize[1]);
};

export const boundingBoxSizeToVerticallyExteriorTextAreaOffset = (
  aspectRatio: number,
  boundingBoxSize: ReadonlyVec2,
) => {
  const graphicSize = getHeightFromAspectRatioAndWidth(aspectRatio, boundingBoxSize[0]);

  // Offset the text area by the size of the graphic from the center of the shape
  return vec2.set(graphicSize, 0, graphicSize[1] / 2);
};

const IDENTITY_MATRIX = mat2d.create();

export const rotateAroundPointMatrix = (
  point: ReadonlyVec2,
  rotation: number,
  out: mat2d = mat2d.create(),
) => {
  mat2dextended.rotateAround(out, IDENTITY_MATRIX, rotation, point);
  return out;
};
